import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AlertCircle, ChevronDown } from 'lucide-react';
import Fieldset from 'components/_UI/Fieldset';
import Switch from 'components/_UI/Switch';
import RichTextEditor from 'components/commons/RichText';
import VariableChips from 'components/commons/VariableChips';
import date from 'helpers/date';
import { SaleTypes } from 'modules/_common/SaleTypes';
import SalePeriodForm from './SalePeriodForm';
import useRichTextImageUpload from './useRichTextImageUpload';
import {
    BUYER_MESSAGE_LINK_LABEL_MAX_LENGTH,
    BUYER_MESSAGE_MAX_LENGTH,
    BUYER_MESSAGE_VARIABLES,
    buyerMessageText,
    buyerMessageToEditorHtml,
    hasPendingBuyerMessageImage,
    isBuyerMessageLinkValid,
} from './buyerMessage';
import { MAX_PER_ORDER_LIMIT, hasSaleStart } from './purchaseRules';
import { HIDDEN_CODE_MAX, OPTION_FIELDS } from './ticketModel';
import { Chip, ERROR, FieldError, FieldHint, INK, LINE, MUTED, accent } from './ticketFormStyles';

const ON_CHIP = { bg: '#F7EFFF', fg: '#6D14CC', border: '#F7EFFF' };

/**
 * As regras menos usadas do ingresso, agrupadas pelo que resolvem e recolhidas.
 *
 * Recolher não pode esconder regra ligada: o cabeçalho lista em chips tudo o
 * que está valendo, e a seção abre sozinha quando um campo dela tem erro.
 */
export default function TicketOptions({ ticket, onChange, errorFor, touch, hasVisibleError, revealKey = 0 }) {
    const [open, setOpen] = useState(false);
    const [hasHiddenCode, setHasHiddenCode] = useState(!!ticket.hidden_code);
    const [hasBuyerMessage, setHasBuyerMessage] = useState(!!(ticket.buyer_message || ticket.buyer_message_link_url));
    const buyerMessageImages = useRichTextImageUpload();
    const buyerMessageEditorRef = useRef(null);
    const id = ticket.internal_id;

    const isPeriod = ticket.sale_type === SaleTypes.PERIOD;
    const saleStartOn = hasSaleStart(ticket);
    const buyerMessageLength = buyerMessageText(ticket.buyer_message).length;
    const isBuyerMessageTooLong = buyerMessageLength > BUYER_MESSAGE_MAX_LENGTH;
    const isBuyerMessageImageUploading = hasPendingBuyerMessageImage(ticket.buyer_message);
    const isBuyerMessageLinkInvalid = !isBuyerMessageLinkValid(ticket.buyer_message_link_url);
    const linkLabelLength = ticket.buyer_message_link_label?.length || 0;

    useEffect(() => {
        if (hasVisibleError) setOpen(true);
        // `revealKey` muda a cada "Ir para o primeiro": reabre mesmo que o
        // produtor tenha recolhido a seção com o erro já visível.
    }, [hasVisibleError, revealKey]);

    function setField(field, value) {
        onChange(previous => ({ ...previous, [field]: value }));
    }

    function toggleSalePeriod(value) {
        setField('sale_type', value ? SaleTypes.PERIOD : SaleTypes.BATCH);
    }

    function toggleMaxPerOrder(value) {
        // Ligar já sugere 1, o caso mais comum (evento gratuito, um por pessoa).
        onChange(previous => ({
            ...previous,
            has_max_per_order: value,
            max_per_order: value && !previous.max_per_order ? '1' : previous.max_per_order,
        }));
    }

    function toggleHiddenCode(value) {
        setHasHiddenCode(value);
        if (!value) setField('hidden_code', null);
    }

    function toggleBuyerMessage(value) {
        setHasBuyerMessage(value);

        if (!value) {
            onChange(previous => ({
                ...previous,
                buyer_message: '',
                buyer_message_link_url: '',
                buyer_message_link_label: '',
            }));
        }
    }

    const summary = [
        !ticket.is_active && 'Inativo',
        isPeriod && 'Período por lote',
        saleStartOn && ticket.opens_at && `Abre ${date.formatDateTime(ticket.opens_at)}`,
        ticket.has_max_per_order && ticket.max_per_order && `Máx. ${ticket.max_per_order} por compra`,
        ticket.requires_document && 'Pede CPF',
        ticket.hidden_code && 'Oculto',
        (ticket.buyer_message || ticket.buyer_message_link_url) && 'Recado',
    ].filter(Boolean);

    const issueCount = OPTION_FIELDS.filter(field => errorFor(field)).length;
    const maxError = errorFor('max_per_order');
    const opensAtError = errorFor('opens_at');
    const hiddenCodeError = errorFor('hidden_code');

    return (
        <Wrapper>
            <Toggle
                type='button'
                aria-expanded={open}
                aria-controls={`options-${id}`}
                onClick={() => setOpen(value => !value)}
            >
                <Chevron open={open}><ChevronDown size={20} aria-hidden='true' /></Chevron>
                <ToggleText>
                    <ToggleTitle>Mais opções</ToggleTitle>
                    {!open && !summary.length && (
                        <ToggleHint>Período de vendas, limite por compra, CPF, ingresso oculto, recado</ToggleHint>
                    )}
                </ToggleText>
                <Chips>
                    {issueCount > 0 && (
                        <Chip bg='#FCEEEE' fg={ERROR} border='#F1C4C1'>
                            {issueCount === 1 ? '1 pendência' : `${issueCount} pendências`}
                        </Chip>
                    )}
                    {summary.length
                        ? summary.map(item => <Chip key={item} {...ON_CHIP}>{item}</Chip>)
                        : !issueCount && <Nothing>Nada ligado</Nothing>}
                </Chips>
            </Toggle>

            {open && (
                <Groups id={`options-${id}`}>
                    <Group>
                        <GroupTitle>Quando vender</GroupTitle>
                        <Option>
                            <Switch id={`period-${id}`} checked={isPeriod} onChange={toggleSalePeriod} />
                            <OptionText htmlFor={`period-${id}`}>
                                <strong>Período de vendas</strong>
                                <span>Cada lote ganha data de início e fim, em vez de virar quando o anterior esgota</span>
                            </OptionText>
                        </Option>
                        {/* O período já tem o início dele, lote a lote. */}
                        {!isPeriod && (
                            <Option>
                                <Switch
                                    id={`opens-${id}`}
                                    checked={saleStartOn}
                                    onChange={value => setField('has_sale_start', value)}
                                />
                                <OptionBody>
                                    <OptionText htmlFor={`opens-${id}`}>
                                        <strong>Início das vendas</strong>
                                        <span>Até a data, aparece como “Em breve”, sem preço e sem botão de compra</span>
                                    </OptionText>
                                    {saleStartOn && (
                                        <Nested data-invalid={opensAtError ? 'true' : undefined}>
                                            <SalePeriodForm
                                                startOnly
                                                salePeriod={{ sale_start: ticket.opens_at, sale_end: null }}
                                                onChangeSalePeriod={({ sale_start }) => {
                                                    setField('opens_at', sale_start);
                                                    touch('opens_at');
                                                }}
                                            />
                                            {opensAtError && <FieldError><AlertCircle size={12} aria-hidden='true' />{opensAtError}</FieldError>}
                                        </Nested>
                                    )}
                                </OptionBody>
                            </Option>
                        )}
                    </Group>

                    <Group>
                        <GroupTitle>Regras de compra</GroupTitle>
                        <Option>
                            <Switch id={`max-${id}`} checked={!!ticket.has_max_per_order} onChange={toggleMaxPerOrder} />
                            <OptionBody>
                                <OptionText htmlFor={`max-${id}`}>
                                    <strong>Limite por compra</strong>
                                    <span>Quantos deste ingresso cabem num pedido. Sem limite, vale o padrão de {MAX_PER_ORDER_LIMIT}.</span>
                                </OptionText>
                                {ticket.has_max_per_order && (
                                    <Nested narrow data-invalid={maxError ? 'true' : undefined}>
                                        <Fieldset
                                            id={`max-value-${id}`}
                                            layout='squired'
                                            label='Máximo por compra'
                                            placeholder='1'
                                            value={ticket.max_per_order || ''}
                                            syncProp
                                            onChangeValue={value => {
                                                setField('max_per_order', value ? value.replace(/\D/g, '') : '');
                                                touch('max_per_order');
                                            }}
                                        />
                                        {maxError
                                            ? <FieldError><AlertCircle size={12} aria-hidden='true' />{maxError}</FieldError>
                                            : <FieldHint>de 1 a {MAX_PER_ORDER_LIMIT} por compra</FieldHint>}
                                    </Nested>
                                )}
                            </OptionBody>
                        </Option>
                        <Option>
                            <Switch
                                id={`cpf-${id}`}
                                checked={!!ticket.requires_document}
                                onChange={value => setField('requires_document', value)}
                            />
                            <OptionText htmlFor={`cpf-${id}`}>
                                <strong>Pedir CPF de cada participante</strong>
                                <span>Um CPF por ingresso na compra — útil para nota fiscal individual</span>
                            </OptionText>
                        </Option>
                    </Group>

                    <Group>
                        <GroupTitle>Visibilidade</GroupTitle>
                        <Option>
                            <Switch id={`active-${id}`} checked={!!ticket.is_active} onChange={value => setField('is_active', value)} />
                            <OptionText htmlFor={`active-${id}`}>
                                <strong>Ingresso ativo</strong>
                                <span>Desligado, sai da página do evento sem perder o que já foi vendido</span>
                            </OptionText>
                        </Option>
                        <Option>
                            <Switch id={`hidden-${id}`} checked={hasHiddenCode} onChange={toggleHiddenCode} />
                            <OptionBody>
                                <OptionText htmlFor={`hidden-${id}`}>
                                    <strong>Ingresso oculto</strong>
                                    <span>Some da página pública; só compra quem tem o código ou o link</span>
                                </OptionText>
                                {hasHiddenCode && (
                                    <Nested data-invalid={hiddenCodeError ? 'true' : undefined}>
                                        <Fieldset
                                            id={`hidden-code-${id}`}
                                            layout='squired'
                                            label='Código oculto'
                                            placeholder='Ex: VIP2026'
                                            value={ticket.hidden_code || ''}
                                            syncProp
                                            onChangeValue={value => {
                                                setField('hidden_code', value || null);
                                                touch('hidden_code');
                                            }}
                                        />
                                        {hiddenCodeError
                                            ? <FieldError><AlertCircle size={12} aria-hidden='true' />{hiddenCodeError}</FieldError>
                                            : <FieldHint>{ticket.hidden_code?.length || 0}/{HIDDEN_CODE_MAX}</FieldHint>}
                                    </Nested>
                                )}
                            </OptionBody>
                        </Option>
                    </Group>

                    <Group>
                        <GroupTitle>Pós-compra</GroupTitle>
                        <Option>
                            <Switch id={`message-${id}`} checked={hasBuyerMessage} onChange={toggleBuyerMessage} />
                            <OptionText htmlFor={`message-${id}`}>
                                <strong>Recado para quem comprar</strong>
                                <span>Texto e link no e-mail de pedido aprovado. Não aparece na página do evento.</span>
                            </OptionText>
                        </Option>
                    </Group>

                    {hasBuyerMessage && (
                        <MessageArea>
                            <FieldHint>
                                Vai no e-mail de pedido aprovado, só para quem comprou este ingresso. Se você editar depois,
                                só os próximos pedidos recebem o texto novo.
                            </FieldHint>
                            <EditorLabel>Mensagem</EditorLabel>
                            <VariableChips
                                variables={BUYER_MESSAGE_VARIABLES}
                                onInsert={token => buyerMessageEditorRef.current?.insertText(token)}
                            />
                            <FieldHint>
                                {'{{nome}}'} vira o nome de quem comprou e {'{{ingresso}}'} o nome deste ingresso, em cada e-mail.
                            </FieldHint>
                            <RichTextEditor
                                ref={buyerMessageEditorRef}
                                placeholder='Ex: Entre no grupo do evento para receber as novidades e o cronograma.'
                                edit={{ description_with_html: buyerMessageToEditorHtml(ticket.buyer_message) }}
                                onChange={value => setField('buyer_message', value)}
                                onChangePureText={() => {}}
                                onToggleImage={buyerMessageImages.upload}
                                descriptionImageUploads={buyerMessageImages.uploads}
                                errorTypeImageDescription={buyerMessageImages.error}
                                setErrorTypeImageDescription={buyerMessageImages.setError}
                            />
                            <FieldHint>{buyerMessageLength}/{BUYER_MESSAGE_MAX_LENGTH}</FieldHint>
                            {isBuyerMessageTooLong && (
                                <FieldError><AlertCircle size={12} aria-hidden='true' />O recado passou de {BUYER_MESSAGE_MAX_LENGTH} caracteres</FieldError>
                            )}
                            {buyerMessageImages.error && (
                                <FieldError><AlertCircle size={12} aria-hidden='true' />{buyerMessageImages.error}</FieldError>
                            )}
                            {isBuyerMessageImageUploading && !buyerMessageImages.error && (
                                <FieldError><AlertCircle size={12} aria-hidden='true' />Enviando a imagem… aguarde para salvar.</FieldError>
                            )}

                            <LinkGrid>
                                <div>
                                    <Fieldset
                                        id={`message-link-${id}`}
                                        layout='squired'
                                        label='Link (opcional)'
                                        placeholder='https://chat.whatsapp.com/...'
                                        value={ticket.buyer_message_link_url || ''}
                                        syncProp
                                        onChangeValue={value => setField('buyer_message_link_url', value)}
                                    />
                                    {isBuyerMessageLinkInvalid && (
                                        <FieldError><AlertCircle size={12} aria-hidden='true' />Link inválido</FieldError>
                                    )}
                                </div>
                                {ticket.buyer_message_link_url && (
                                    <div>
                                        <Fieldset
                                            id={`message-label-${id}`}
                                            layout='squired'
                                            label='Texto do botão (opcional)'
                                            placeholder='Acessar link'
                                            value={ticket.buyer_message_link_label || ''}
                                            syncProp
                                            maxLength={BUYER_MESSAGE_LINK_LABEL_MAX_LENGTH}
                                            onChangeValue={value => setField('buyer_message_link_label', value)}
                                        />
                                        <FieldHint>{linkLabelLength}/{BUYER_MESSAGE_LINK_LABEL_MAX_LENGTH}</FieldHint>
                                    </div>
                                )}
                            </LinkGrid>
                        </MessageArea>
                    )}
                </Groups>
            )}
        </Wrapper>
    );
}

const Wrapper = styled.section`
    border-top: 1px solid #ECECF0;
    padding-top: 8px;
`;

const Toggle = styled.button`
    width: 100%;
    min-height: 56px;
    padding: 6px 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 12px;
    background: transparent;
    border: 0;
    border-radius: 10px;
    cursor: pointer;
    font-family: inherit;
    text-align: left;

    &:focus-visible {
        outline: 2px solid ${accent};
        outline-offset: 2px;
    }
`;

const Chevron = styled.span.withConfig({
    shouldForwardProp: prop => prop !== 'open',
})`
    display: flex;
    color: #3A3A42;
    transform: rotate(${({ open }) => (open ? '0deg' : '-90deg')});
    transition: transform 0.15s ease;
`;

const ToggleText = styled.span`
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1 1 200px;
`;

const ToggleTitle = styled.span`
    font-size: 15px;
    font-weight: 600;
    color: ${INK};
`;

const ToggleHint = styled.span`
    font-size: 13px;
    color: ${MUTED};
`;

const Chips = styled.span`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

const Nothing = styled.span`
    font-size: 13px;
    color: ${MUTED};
`;

const Groups = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 20px 32px;
    padding: 8px 0 8px 32px;

    @media screen and (min-width: 1100px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media screen and (max-width: 620px) {
        padding-left: 0;
    }
`;

const Group = styled.div`
    display: flex;
    flex-direction: column;

    & > div + div {
        border-top: 1px solid #F0F0F3;
    }
`;

const GroupTitle = styled.h4`
    margin: 0 0 4px;
    font-size: 14px;
    font-weight: 600;
    color: ${INK};
`;

const Option = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 10px 0;

    /* O Switch compartilhado traz margem própria; aqui o gap já espaça. */
    & > div:first-child {
        width: auto;
        flex: 0 0 auto;
        margin: 2px 0 0;
    }
`;

const OptionBody = styled.div`
    flex: 1 1 auto;
    min-width: 0;
`;

const OptionText = styled.label`
    display: flex;
    flex-direction: column;
    gap: 2px;
    cursor: pointer;

    strong {
        font-size: 14px;
        font-weight: 500;
        color: ${INK};
    }

    span {
        font-size: 13px;
        line-height: 1.4;
        color: ${MUTED};
    }
`;

const Nested = styled.div.withConfig({
    shouldForwardProp: prop => prop !== 'narrow',
})`
    margin-top: 10px;
    max-width: ${({ narrow }) => (narrow ? '220px' : 'none')};
`;

const MessageArea = styled.div`
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 14px;
    border: 1px solid ${LINE};
    border-radius: 12px;
`;

const EditorLabel = styled.p`
    margin: 4px 0 0;
    font-size: 14px;
    font-weight: 700;
    color: ${INK};
`;

const LinkGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 12px;
    margin-top: 4px;
`;
